import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useDeletePatientPhoto, useUploadPatientPhoto } from "@/lib/mutations";
import { usePatientPhotoBlob } from "@/lib/queries";
import { Button } from "@/components/ui/button";
import { Camera, Trash, LoaderCircle, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { PatientPhotoCaptureDialog } from "./PatientPhotoCaptureDialog";

interface PatientPhotoCardProps {
  patientId?: string;
  photoR2Key?: string | null;
  /** Emitted for the add-patient form flow (patient not yet created). */
  onPhotoStaged?: (blob: Blob) => void;
  /** Compact header variant (patient detail page). */
  variant?: "card" | "compact";
  className?: string;
}

/**
 * Passport-photo placeholder/live photo with Add / Replace / Remove actions.
 * Two modes:
 * - Existing patient (patientId set): uploads/removals go straight to the
 *   API (perm-gated: photos.create / photos.edit / photos.delete).
 * - New patient (add-patient form): the finalized blob is staged in memory
 *   and uploaded right after the patient is created.
 */
export function PatientPhotoCard({
  patientId, photoR2Key, onPhotoStaged, variant = "card", className,
}: PatientPhotoCardProps) {
  const { isAdmin, permissions } = useAuth();
  const canAdd = isAdmin || permissions.has("photos.create");
  const canReplace = isAdmin || permissions.has("photos.edit");
  const canRemove = isAdmin || permissions.has("photos.delete");

  const [captureOpen, setCaptureOpen] = useState(false);
  const uploadMut = useUploadPatientPhoto();
  const removeMut = useDeletePatientPhoto(patientId ?? "");

  // Photo blob + object URL (existing patient only; new-patient photos come
  // from the capture dialog and are never fetched). URL lifecycle is owned
  // here: created when the blob changes, revoked on the next change/unmount.
  const photoQuery = usePatientPhotoBlob(patientId, photoR2Key);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!photoQuery.data) { setPhotoUrl(null); return; }
    const url = URL.createObjectURL(photoQuery.data);
    setPhotoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [photoQuery.data]);

  const hasPhoto = !!photoR2Key;
  const replacing = hasPhoto; // dialog label context
  const canCapture = hasPhoto ? canReplace : canAdd;

  const handleConfirm = async (blob: Blob) => {
    if (patientId) {
      await uploadMut.mutateAsync({ patientId, blob });
    } else {
      onPhotoStaged?.(blob);
    }
  };

  const handleRemove = async () => {
    if (!patientId) return;
    try {
      await removeMut.mutateAsync();
    } catch { /* toast handled in mutation */ }
  };

  if (variant === "compact") {
    return (
      <div className={cn("flex items-center gap-3", className)}>
        <PhotoThumb
          url={photoUrl}
          hasPhoto={hasPhoto}
          onClick={() => canCapture && setCaptureOpen(true)}
          interactive={canCapture}
        />
        <div className="flex flex-col gap-1">
          {canCapture && (
            <Button size="sm" variant="outline" onClick={() => setCaptureOpen(true)}>
              <Camera size={14} className="mr-1" /> {hasPhoto ? "Replace photo" : "Add photo"}
            </Button>
          )}
          {hasPhoto && canRemove && (
            <Button size="sm" variant="ghost" className="text-red-600" onClick={handleRemove} disabled={removeMut.isPending}>
              {removeMut.isPending ? <LoaderCircle size={14} className="mr-1 animate-spin" /> : <Trash size={14} className="mr-1" />}
              Remove
            </Button>
          )}
        </div>
        <PatientPhotoCaptureDialog
          open={captureOpen} onOpenChange={setCaptureOpen}
          onConfirm={handleConfirm} replacing={replacing}
        />
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col items-center gap-2", className)}>
      <div className="relative">
        <PhotoThumb
          url={photoUrl}
          hasPhoto={hasPhoto}
          onClick={() => canCapture && setCaptureOpen(true)}
          interactive={canCapture}
          large
        />
        {photoQuery.isFetching && (
          <div className="absolute inset-0 flex items-center justify-center rounded-md bg-black/30">
            <LoaderCircle size={20} className="animate-spin text-cream-50" />
          </div>
        )}
      </div>
      <div className="flex flex-col items-center gap-1">
        {canCapture && (
          <Button size="sm" variant="outline" onClick={() => setCaptureOpen(true)}>
            <Camera size={14} className="mr-1" /> {hasPhoto ? "Replace photo" : "Add photo"}
          </Button>
        )}
        {hasPhoto && canRemove && (
          <Button size="sm" variant="ghost" className="text-red-600" onClick={handleRemove} disabled={removeMut.isPending}>
            {removeMut.isPending ? <LoaderCircle size={14} className="mr-1 animate-spin" /> : <Trash size={14} className="mr-1" />}
            Remove
          </Button>
        )}
        {!canCapture && !canRemove && (
          <p className="text-[10px] text-muted">Photo not available</p>
        )}
      </div>
      <PatientPhotoCaptureDialog
        open={captureOpen} onOpenChange={setCaptureOpen}
        onConfirm={handleConfirm} replacing={replacing}
      />
    </div>
  );
}

function PhotoThumb({
  url, hasPhoto, onClick, interactive, large,
}: {
  url?: string | null; hasPhoto: boolean; onClick?: () => void; interactive?: boolean; large?: boolean;
}) {
  const size = large ? "h-[159px] w-[124px]" : "h-[96px] w-[74px]";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!interactive}
      className={cn(
        "relative overflow-hidden rounded-md border border-cream-200 bg-cream-50 dark:border-dark-700 dark:bg-dark-800",
        size,
        interactive && "cursor-pointer hover:border-maroon-600",
        !interactive && "cursor-default",
      )}
      title={hasPhoto ? "View/replace photo" : "Add photo"}
    >
      {hasPhoto && url ? (
        <img src={url} alt="Patient photo" className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-muted">
          <User size={large ? 40 : 28} />
          {large && <span className="text-[10px]">No photo</span>}
        </div>
      )}
    </button>
  );
}
